
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import BackButton from '../common/BackButton';
import {
  Save,
  AlertCircle,
  Package,
  DollarSign,
  Battery,
  Plus,
  Trash2,
  MapPin,
} from 'lucide-react';
import { batteriesAPI, batteryBrandsAPI, locationsAPI, madeInsAPI, zonesAPI } from '../../services/garageApi';

const EditBattery = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  // Dropdown data
  const [batteryBrands, setBatteryBrands] = useState([]);
  const [locations, setLocations] = useState([]);
  const [madeIns, setMadeIns] = useState([]);

  // Distribution state
  const [totalQuantity, setTotalQuantity] = useState('');
  const [distributions, setDistributions] = useState([
    { location_id: '', zone_id: '', quantity: '' }
  ]);
  const [zonesCache, setZonesCache] = useState({});
  const [inventoryChanged, setInventoryChanged] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    battery_name: '',
    battery_brand_id: '',
    made_in_id: '',
    date_received: '',
    cost_price: '',
    selling_price: '',
    tax_rate: '',
    is_tax_inclusive: false,
    notes: '',
    active: true,
    battery_shape: '',
    product_shape: '',
    ampere: '',
    reversed: false,
    battery_type: '',
  });

  const getIdFromField = (field) => {
    if (!field) return '';
    if (typeof field === 'string' || typeof field === 'number') return String(field);
    if (typeof field === 'object' && field.id !== undefined) return String(field.id);
    return '';
  };

  const formatDateForInput = (dateValue) => {
    if (!dateValue) return '';

    if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      return dateValue;
    }

    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return '';

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  };

  // Use useCallback to prevent infinite re-renders
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const [batteryRes, brandsRes, locationsRes, madeInsRes] = await Promise.all([
        batteriesAPI.show(id),
        batteryBrandsAPI.getAll(),
        locationsAPI.getAll(),
        madeInsAPI.getAll()
      ]);

      // Extract battery data from response
      let battery = null;
      if (batteryRes.data) {
        // Check for success wrapper
        if (batteryRes.data.success && batteryRes.data.data) {
          battery = batteryRes.data.data;
        }
        // Direct data
        else if (batteryRes.data.data) {
          battery = batteryRes.data.data;
        }
        // Direct battery object
        else if (batteryRes.data.id) {
          battery = batteryRes.data;
        }
      }

      // Process battery data
      if (battery) {
        // Build distributions from inventories
        let totalQty = battery.quantity || 0;
        const distRows = [];

        if (battery.inventories && Array.isArray(battery.inventories) && battery.inventories.length > 0) {
          totalQty = battery.inventories.reduce((sum, inv) => sum + (parseInt(inv.quantity) || 0), 0);
          for (const inv of battery.inventories) {
            const locId = getIdFromField(inv.location_id) || getIdFromField(inv.location);
            const zoneId = getIdFromField(inv.zone_id) || getIdFromField(inv.zone);
            distRows.push({
              location_id: locId,
              zone_id: zoneId,
              quantity: String(inv.quantity || 0),
            });
            if (locId) {
              try {
                const zoneResp = await zonesAPI.getByLocation(locId);
                const zonesData = zoneResp?.data?.data?.data || zoneResp?.data?.data || zoneResp?.data || [];
                setZonesCache(prev => ({ ...prev, [locId]: Array.isArray(zonesData) ? zonesData : [] }));
              } catch (e) {
                setZonesCache(prev => ({ ...prev, [locId]: [] }));
              }
            }
          }
        }

        setTotalQuantity(String(totalQty));
        if (distRows.length > 0) {
          setDistributions(distRows);
        } else {
          setDistributions([{ location_id: '', zone_id: '', quantity: '' }]);
        }

        const processedFormData = {
          battery_name: battery.battery_name || '',
          battery_brand_id: getIdFromField(battery.battery_brand || battery.batteryBrand) ||
              getIdFromField(battery.battery_brand_id) || '',
          made_in_id: getIdFromField(battery.made_in || battery.madeIn) ||
              getIdFromField(battery.made_in_id) || '',
          date_received: formatDateForInput(battery.date_received),
          cost_price: battery.cost_price || '',
          selling_price: battery.selling_price || '',
          tax_rate: battery.tax_rate?.toString() || '',
          is_tax_inclusive: !!battery.is_tax_inclusive,
          notes: battery.notes || '',
          active: battery.active === 1 || battery.active === true || battery.active === '1',
          battery_shape: battery.battery_shape || battery.product_shape || '',
          product_shape: battery.product_shape || battery.battery_shape || '',
          ampere: battery.ampere || '',
          reversed: battery.reversed === 1 || battery.reversed === true || battery.reversed === '1',
          battery_type: battery.battery_type || '',
        };

        setFormData(processedFormData);
      }

      // Extract dropdown data
      const extractDropdownData = (response) => {
        if (!response || !response.data) return [];

        // Check for success wrapper with paginated data
        if (response.data.success && response.data.data) {
          if (response.data.data.data && Array.isArray(response.data.data.data)) {
            return response.data.data.data; // Paginated
          }
          if (Array.isArray(response.data.data)) {
            return response.data.data; // Direct array
          }
        }
        // Direct data
        if (response.data.data && Array.isArray(response.data.data)) {
          return response.data.data;
        }
        if (Array.isArray(response.data)) {
          return response.data;
        }
        return [];
      };

      const brandsData = extractDropdownData(brandsRes);
      const locationsData = extractDropdownData(locationsRes);
      const madeInsData = extractDropdownData(madeInsRes);

      setBatteryBrands(brandsData);
      setLocations(locationsData);
      setMadeIns(madeInsData);

    } catch (error) {
      console.error('Error fetching data:', error);
      setErrors({submit: 'Failed to load battery data. Please try again.'});
    } finally {
      setLoading(false);
    }
  }, [id]); // Only depends on id

  // Use the callback in useEffect - runs only once when id changes
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchZonesForLocation = useCallback(async (locationId) => {
    if (!locationId) return;
    if (zonesCache[locationId]) return;
    try {
      const response = await zonesAPI.getByLocation(locationId);
      const zonesData = response?.data?.data?.data || response?.data?.data || response?.data || [];
      setZonesCache(prev => ({ ...prev, [locationId]: Array.isArray(zonesData) ? zonesData : [] }));
    } catch (error) {
      setZonesCache(prev => ({ ...prev, [locationId]: [] }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zonesCache]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Build a set of used location/zone combination keys (excluding a given row index)
  const getUsedCombinations = (excludeIndex = -1) => {
    const used = new Set();
    distributions.forEach((d, i) => {
      if (i !== excludeIndex && d.location_id) {
        const key = `${d.location_id}-${d.zone_id || ''}`;
        used.add(key);
      }
    });
    return used;
  };

  const isCombinationUsed = (locationId, zoneId, excludeIndex) => {
    const key = `${locationId}-${zoneId || ''}`;
    const used = getUsedCombinations(excludeIndex);
    return used.has(key);
  };

  // Distribution handlers
  const handleDistributionChange = (index, field, value) => {
    setInventoryChanged(true);
    const updated = [...distributions];
    updated[index] = { ...updated[index], [field]: value };

    if (field === 'location_id') {
      updated[index].zone_id = '';
      if (value) fetchZonesForLocation(value);
    }

    const newLocId = updated[index].location_id;
    const newZoneId = updated[index].zone_id;
    if (newLocId) {
      const dupKey = `dist_${index}_duplicate`;
      const isDup = isCombinationUsed(newLocId, newZoneId, index);
      if (isDup) {
        setErrors(prev => ({ ...prev, [dupKey]: 'This location/zone combination has already been added to the distribution list.' }));
      } else {
        setErrors(prev => {
          const next = { ...prev };
          delete next[dupKey];
          distributions.forEach((_, otherIdx) => {
            if (otherIdx !== index) {
              const otherKey = `dist_${otherIdx}_duplicate`;
              if (next[otherKey]) {
                const otherLoc = updated[otherIdx]?.location_id;
                const otherZone = updated[otherIdx]?.zone_id;
                const otherCombo = `${otherLoc}-${otherZone || ''}`;
                const count = updated.filter((d, j) => j !== otherIdx && d.location_id && `${d.location_id}-${d.zone_id || ''}` === otherCombo).length;
                if (count === 0) delete next[otherKey];
              }
            }
          });
          return next;
        });
      }
    }

    setDistributions(updated);

    if (errors.distributions) {
      setErrors(prev => ({ ...prev, distributions: '' }));
    }
  };

  const addDistributionRow = () => {
    setInventoryChanged(true);
    setDistributions(prev => [...prev, { location_id: '', zone_id: '', quantity: '' }]);
  };

  const removeDistributionRow = (index) => {
    if (distributions.length <= 1) return;
    setInventoryChanged(true);
    const updated = distributions.filter((_, i) => i !== index);
    setDistributions(updated);

    setErrors(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(key => {
        if (key.startsWith(`dist_${index}_`)) delete next[key];
      });
      Object.keys(next).forEach(key => {
        const match = key.match(/^dist_(\d+)_duplicate$/);
        if (match) delete next[key];
      });
      return next;
    });
  };

  const getAllocatedQuantity = () => {
    return distributions.reduce((sum, d) => sum + (parseInt(d.quantity) || 0), 0);
  };

  const getRemainingQuantity = () => {
    const total = parseInt(totalQuantity) || 0;
    return total - getAllocatedQuantity();
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.battery_name) newErrors.battery_name = 'Battery name is required';
    if (!formData.battery_brand_id) newErrors.battery_brand_id = 'Brand is required';
    if (!formData.made_in_id) newErrors.made_in_id = 'Origin is required';
    if (!formData.date_received) newErrors.date_received = 'Date received is required';
    if (!formData.battery_shape && !formData.product_shape) {
      newErrors.battery_shape = 'Battery shape is required';
    }
    if (!formData.ampere) newErrors.ampere = 'Ampere is required';
    if (!formData.battery_type) newErrors.battery_type = 'Battery type is required';
    if (!formData.cost_price) newErrors.cost_price = 'Cost price is required';
    if (!formData.selling_price) newErrors.selling_price = 'Selling price is required';
    if (!totalQuantity || parseInt(totalQuantity) < 1) {
      newErrors.total_quantity = 'Total quantity must be at least 1';
    }

    // Validate distributions only if user has filled in any row
    const hasAnyDistribution = distributions.some(d => d.location_id || d.quantity);
    if (hasAnyDistribution) {
      let hasDistError = false;
      const seenCombos = new Set();
      distributions.forEach((d, i) => {
        if (d.location_id || d.quantity) {
          if (!d.location_id) {
            newErrors[`dist_${i}_location`] = 'Location is required';
            hasDistError = true;
          }
          if (!d.quantity || parseInt(d.quantity) < 1) {
            newErrors[`dist_${i}_quantity`] = 'Quantity must be at least 1';
            hasDistError = true;
          }
          if (d.location_id) {
            const comboKey = `${d.location_id}-${d.zone_id || ''}`;
            if (seenCombos.has(comboKey)) {
              newErrors[`dist_${i}_duplicate`] = 'This location/zone combination has already been added to the distribution list.';
              hasDistError = true;
            }
            seenCombos.add(comboKey);
          }
        }
      });
      if (!hasDistError && totalQuantity && getRemainingQuantity() !== 0) {
        newErrors.distributions = `Allocated quantity (${getAllocatedQuantity()}) must equal total quantity (${totalQuantity})`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      const firstError = document.querySelector('.border-red-300');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setSaving(true);
    try {
      const submitData = new FormData();

      // Battery product fields
      // NOTE: _method=PUT is appended by batteriesAPI.update() (garageApi.js) for FormData payloads.
      submitData.append('battery_name', formData.battery_name);
      submitData.append('battery_brand_id', formData.battery_brand_id);
      submitData.append('made_in_id', formData.made_in_id);
      submitData.append('battery_shape', formData.battery_shape || formData.product_shape);
      submitData.append('product_shape', formData.battery_shape || formData.product_shape);
      submitData.append('ampere', parseInt(formData.ampere) || 0);
      submitData.append('reversed', formData.reversed ? '1' : '0');
      submitData.append('battery_type', formData.battery_type);
      submitData.append('date_received', formData.date_received);
      submitData.append('cost_price', parseFloat(formData.cost_price) || 0);
      submitData.append('selling_price', parseFloat(formData.selling_price) || 0);
      submitData.append('tax_rate', formData.tax_rate || '0');
      submitData.append('is_tax_inclusive', formData.is_tax_inclusive ? '1' : '0');
      submitData.append('notes', formData.notes || '');
      submitData.append('active', formData.active ? '1' : '0');
      submitData.append('total_quantity', totalQuantity);

      // Only send inventory data when the user actually changed it
      if (inventoryChanged) {
        submitData.append('_inventory_changed', '1');
        const filledDist = distributions.filter(d => d.location_id && d.quantity);
        if (filledDist.length > 0) {
          submitData.append('distributions', JSON.stringify(filledDist.map(d => ({
            location_id: d.location_id,
            zone_id: d.zone_id || null,
            quantity: parseInt(d.quantity),
          }))));
        }
      }

      await batteriesAPI.update(id, submitData);

      navigate('/batteries', {
        state: { message: 'Battery product updated successfully!' }
      });
    } catch (error) {
      console.error('Error updating battery:', error);
      if (error.response?.data?.errors) {
        const errorMessages = Object.entries(error.response.data.errors)
            .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
            .join('\n');
        setErrors({ submit: errorMessages });
      } else {
        setErrors({ submit: error.response?.data?.message || 'Failed to update battery. Please try again.' });
      }
    } finally {
      setSaving(false);
    }
  };

  const calculateProfit = () => {
    const cost = parseFloat(formData.cost_price) || 0;
    const selling = parseFloat(formData.selling_price) || 0;
    const taxRate = parseFloat(formData.tax_rate) || 0;
    const profit = selling - cost;
    const margin = cost > 0 ? (profit / cost * 100).toFixed(2) : 0;
    const finalPrice = formData.is_tax_inclusive
        ? selling
        : selling * (1 + taxRate / 100);
    return { profit, margin, finalPrice };
  };

  if (loading) {
    return (
        <div className="flex justify-center items-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
    );
  }

  return (
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3">
            <BackButton to="/batteries" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Edit Battery Product</h1>
              <p className="text-sm text-gray-600 mt-1">Update battery information and inventory</p>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
            {errors.submit && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2 text-red-700">
                  <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span className="whitespace-pre-line">{errors.submit}</span>
                </div>
            )}

            {/* Basic Information Section */}
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Package className="w-5 h-5 text-gray-600" />
                <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Battery Name <span className="text-red-500">*</span>
                  </label>
                  <input
                      type="text"
                      name="battery_name"
                      value={formData.battery_name}
                      onChange={handleChange}
                      className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors.battery_name ? 'border-red-300' : 'border-gray-200'
                      }`}
                  />
                  {errors.battery_name && (
                      <p className="mt-1 text-sm text-red-600">{errors.battery_name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Battery Brand <span className="text-red-500">*</span>
                  </label>
                  <select
                      name="battery_brand_id"
                      value={formData.battery_brand_id}
                      onChange={handleChange}
                      className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors.battery_brand_id ? 'border-red-300' : 'border-gray-200'
                      }`}
                  >
                    <option value="">Select Brand</option>
                    {batteryBrands.map(brand => (
                        <option key={brand.id} value={String(brand.id)}>{brand.name}</option>
                    ))}
                  </select>
                  {errors.battery_brand_id && (
                      <p className="mt-1 text-sm text-red-600">{errors.battery_brand_id}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Made In <span className="text-red-500">*</span>
                  </label>
                  <select
                      name="made_in_id"
                      value={formData.made_in_id}
                      onChange={handleChange}
                      className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors.made_in_id ? 'border-red-300' : 'border-gray-200'
                      }`}
                  >
                    <option value="">Select Origin</option>
                    {madeIns.length > 0 ? (
                        madeIns.map(madeIn => (
                            <option key={madeIn.id} value={String(madeIn.id)}>{madeIn.name}</option>
                        ))
                    ) : (
                        <option disabled>No origins available</option>
                    )}
                  </select>
                  {errors.made_in_id && (
                      <p className="mt-1 text-sm text-red-600">{errors.made_in_id}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date Received <span className="text-red-500">*</span>
                  </label>
                  <input
                      type="date"
                      name="date_received"
                      value={formData.date_received}
                      onChange={handleChange}
                      className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors.date_received ? 'border-red-300' : 'border-gray-200'
                      }`}
                  />
                  {errors.date_received && (
                      <p className="mt-1 text-sm text-red-600">{errors.date_received}</p>
                  )}
                </div>

                <div className="flex items-center">
                  <label className="flex items-center cursor-pointer">
                    <input
                        type="checkbox"
                        name="active"
                        checked={formData.active}
                        onChange={handleChange}
                        className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm font-medium text-gray-700">
                    Active Product
                  </span>
                  </label>
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes
                </label>
                <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    rows="3"
                    placeholder="Add any additional notes..."
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Battery Specifications Section */}
            <div className="mb-8 border-t pt-8">
              <div className="flex items-center gap-2 mb-4">
                <Battery className="w-5 h-5 text-gray-600" />
                <h3 className="text-lg font-semibold text-gray-900">Battery Specifications</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Battery Shape <span className="text-red-500">*</span>
                  </label>
                  <select
                      name="battery_shape"
                      value={formData.battery_shape || formData.product_shape}
                      onChange={handleChange}
                      className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors.battery_shape ? 'border-red-300' : 'border-gray-200'
                      }`}
                  >
                    <option value="">Select Shape</option>
                    <option value="thin">Thin</option>
                    <option value="thick">Thick</option>
                  </select>
                  {errors.battery_shape && (
                      <p className="mt-1 text-sm text-red-600">{errors.battery_shape}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ampere (AH) <span className="text-red-500">*</span>
                  </label>
                  <input
                      type="number"
                      name="ampere"
                      value={formData.ampere}
                      onChange={handleChange}
                      min="0"
                      className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors.ampere ? 'border-red-300' : 'border-gray-200'
                      }`}
                  />
                  {errors.ampere && (
                      <p className="mt-1 text-sm text-red-600">{errors.ampere}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Battery Type <span className="text-red-500">*</span>
                  </label>
                  <select
                      name="battery_type"
                      value={formData.battery_type}
                      onChange={handleChange}
                      className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors.battery_type ? 'border-red-300' : 'border-gray-200'
                      }`}
                  >
                    <option value="">Select Type</option>
                    <option value="gel">Gel</option>
                    <option value="acid">Acid</option>
                    <option value="lithium">Lithium</option>
                    <option value="agm">AGM</option>
                    <option value="efb">EFB</option>
                  </select>
                  {errors.battery_type && (
                      <p className="mt-1 text-sm text-red-600">{errors.battery_type}</p>
                  )}
                </div>

                <div className="flex items-center">
                  <label className="flex items-center cursor-pointer">
                    <input
                        type="checkbox"
                        name="reversed"
                        checked={formData.reversed}
                        onChange={handleChange}
                        className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm font-medium text-gray-700">
                    Reversed Terminal
                  </span>
                  </label>
                </div>
              </div>
            </div>

            {/* Pricing & Inventory Section */}
            <div className="mb-8 border-t pt-8">
              <div className="flex items-center gap-2 mb-4">
                <DollarSign className="w-5 h-5 text-gray-600" />
                <h3 className="text-lg font-semibold text-gray-900">Pricing & Inventory</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cost Price <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <DollarSign className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                        type="number"
                        step="0.01"
                        name="cost_price"
                        value={formData.cost_price}
                        onChange={handleChange}
                        placeholder="0.00"
                        className={`w-full pl-10 pr-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            errors.cost_price ? 'border-red-300' : 'border-gray-200'
                        }`}
                    />
                  </div>
                  {errors.cost_price && (
                      <p className="mt-1 text-sm text-red-600">{errors.cost_price}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Selling Price <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <DollarSign className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                        type="number"
                        step="0.01"
                        name="selling_price"
                        value={formData.selling_price}
                        onChange={handleChange}
                        placeholder="0.00"
                        className={`w-full pl-10 pr-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            errors.selling_price ? 'border-red-300' : 'border-gray-200'
                        }`}
                    />
                  </div>
                  {errors.selling_price && (
                      <p className="mt-1 text-sm text-red-600">{errors.selling_price}</p>
                  )}
                </div>
              </div>

              {/* Tax Configuration */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tax Rate (%)
                  </label>
                  <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      name="tax_rate"
                      value={formData.tax_rate}
                      onChange={handleChange}
                      placeholder="0.00"
                      className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors.tax_rate ? 'border-red-300' : 'border-gray-200'
                      }`}
                  />
                  {errors.tax_rate && <p className="mt-1 text-sm text-red-600">{errors.tax_rate}</p>}
                </div>
                <div className="flex items-center">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                        type="checkbox"
                        name="is_tax_inclusive"
                        checked={formData.is_tax_inclusive}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          is_tax_inclusive: e.target.checked
                        }))}
                        className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    <span className="ml-3 text-sm font-medium text-gray-700">Tax Inclusive</span>
                  </label>
                  <p className="ml-2 text-xs text-gray-500">
                    {formData.is_tax_inclusive
                        ? 'Selling price already includes tax'
                        : 'Tax will be added to selling price'}
                  </p>
                </div>
              </div>

              {formData.cost_price && formData.selling_price && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-blue-900">Profit Margin</p>
                        <p className="text-2xl font-bold text-blue-900">
                          ${calculateProfit().profit.toFixed(2)}
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-blue-900">Margin %</p>
                        <p className="text-2xl font-bold text-blue-900">
                          {calculateProfit().margin}%
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-blue-900">Final Price (incl. tax)</p>
                        <p className="text-xs text-blue-700 mt-0.5">
                          {formData.is_tax_inclusive
                              ? 'Tax included in selling price'
                              : `Selling Price + ${formData.tax_rate || 0}% tax`}
                        </p>
                        <p className="text-2xl font-bold text-green-700">
                          ${calculateProfit().finalPrice.toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>
              )}

              {/* Stock Distribution */}
              <div className="mt-6 border-t pt-6">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Stock Distribution
                  </h4>
                </div>
                <p className="text-xs text-gray-500 mb-4">
                  Optional: Specify where to store the stock. If left empty, all stock will be placed in the Unallocated zone for later distribution.
                </p>

                {/* Total Quantity */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Total Quantity <span className="text-red-500">*</span>
                  </label>
                  <input
                      type="number"
                      value={totalQuantity}
                      onChange={(e) => {
                        setTotalQuantity(e.target.value);
                        setInventoryChanged(true);
                        if (errors.total_quantity) setErrors(prev => ({ ...prev, total_quantity: '' }));
                      }}
                      placeholder="Enter total stock quantity"
                      min="1"
                      className={`w-full max-w-xs px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors.total_quantity ? 'border-red-300' : 'border-gray-200'
                      }`}
                  />
                  {errors.total_quantity && <p className="mt-1 text-sm text-red-600">{errors.total_quantity}</p>}
                </div>

                {/* Remaining Counter */}
                {totalQuantity && parseInt(totalQuantity) > 0 && (
                    <div className={`mb-4 p-3 rounded-lg border ${
                        getRemainingQuantity() === 0
                            ? 'bg-green-50 border-green-200 text-green-700'
                            : getRemainingQuantity() < 0
                                ? 'bg-red-50 border-red-200 text-red-700'
                                : 'bg-yellow-50 border-yellow-200 text-yellow-700'
                    }`}>
                      <div className="flex items-center justify-between text-sm font-medium">
                        <span>Allocated: {getAllocatedQuantity()} / {totalQuantity}</span>
                        <span>
                          {getRemainingQuantity() === 0
                              ? 'Fully allocated'
                              : getRemainingQuantity() > 0
                                  ? `${getRemainingQuantity()} remaining`
                                  : `${Math.abs(getRemainingQuantity())} over-allocated`}
                        </span>
                      </div>
                      <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                        <div
                            className={`h-2 rounded-full transition-all ${
                                getRemainingQuantity() === 0
                                    ? 'bg-green-500'
                                    : getRemainingQuantity() < 0
                                        ? 'bg-red-500'
                                        : 'bg-yellow-500'
                            }`}
                            style={{ width: `${Math.min(100, (getAllocatedQuantity() / parseInt(totalQuantity)) * 100)}%` }}
                        />
                      </div>
                    </div>
                )}

                {errors.distributions && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      {errors.distributions}
                    </div>
                )}

                {/* Distribution Rows */}
                <div className="space-y-3">
                  {distributions.map((dist, index) => {
                    const usedCombos = getUsedCombinations(index);
                    const hasDuplicateError = !!errors[`dist_${index}_duplicate`];

                    return (
                        <div key={index} className={`flex items-start gap-3 p-4 rounded-lg border ${
                            hasDuplicateError ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'
                        }`}>
                          <div className="flex-1">
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Location
                            </label>
                            <select
                                value={dist.location_id}
                                onChange={(e) => handleDistributionChange(index, 'location_id', e.target.value)}
                                className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                    errors[`dist_${index}_location`] || hasDuplicateError ? 'border-red-300' : 'border-gray-200'
                                }`}
                            >
                              <option value="">Select Location</option>
                              {locations.map(loc => {
                                const locationZones = zonesCache[String(loc.id)] || [];
                                const noZoneUsed = usedCombos.has(`${loc.id}-`);
                                const allZonesUsed = locationZones.length > 0
                                    ? locationZones.every(z => usedCombos.has(`${loc.id}-${z.id}`))
                                    : noZoneUsed;
                                return (
                                    <option
                                        key={loc.id}
                                        value={String(loc.id)}
                                        disabled={allZonesUsed && String(loc.id) !== String(dist.location_id)}
                                    >
                                      {loc.name}{allZonesUsed && String(loc.id) !== String(dist.location_id) ? ' (all zones used)' : ''}
                                    </option>
                                );
                              })}
                            </select>
                            {errors[`dist_${index}_location`] && (
                                <p className="mt-1 text-xs text-red-600">{errors[`dist_${index}_location`]}</p>
                            )}
                          </div>

                          <div className="flex-1">
                            <label className="block text-xs font-medium text-gray-600 mb-1">Zone</label>
                            <select
                                value={dist.zone_id}
                                onChange={(e) => handleDistributionChange(index, 'zone_id', e.target.value)}
                                className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                    hasDuplicateError ? 'border-red-300' : 'border-gray-200'
                                }`}
                                disabled={!dist.location_id || !(zonesCache[dist.location_id]?.length > 0)}
                            >
                              <option value="">
                                {!dist.location_id
                                    ? 'Select location first'
                                    : zonesCache[dist.location_id]?.length > 0
                                        ? 'Select Zone (optional)'
                                        : 'No zones available'}
                              </option>
                              {(zonesCache[dist.location_id] || []).map(zone => {
                                const zoneUsed = usedCombos.has(`${dist.location_id}-${zone.id}`);
                                return (
                                    <option
                                        key={zone.id}
                                        value={String(zone.id)}
                                        disabled={zoneUsed}
                                    >
                                      {zone.name}{zoneUsed ? ' (already used)' : ''}
                                    </option>
                                );
                              })}
                            </select>
                            {hasDuplicateError && (
                                <p className="mt-1 text-xs text-red-600">{errors[`dist_${index}_duplicate`]}</p>
                            )}
                          </div>

                          <div className="w-32">
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Qty
                            </label>
                            <input
                                type="number"
                                value={dist.quantity}
                                onChange={(e) => handleDistributionChange(index, 'quantity', e.target.value)}
                                placeholder="0"
                                min="1"
                                className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                    errors[`dist_${index}_quantity`] ? 'border-red-300' : 'border-gray-200'
                                }`}
                            />
                            {errors[`dist_${index}_quantity`] && (
                                <p className="mt-1 text-xs text-red-600">{errors[`dist_${index}_quantity`]}</p>
                            )}
                          </div>

                          {distributions.length > 1 && (
                              <button
                                  type="button"
                                  onClick={() => removeDistributionRow(index)}
                                  className="mt-6 p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                          )}
                        </div>
                    );
                  })}
                </div>

                <button
                    type="button"
                    onClick={addDistributionRow}
                    className="mt-3 px-4 py-2 text-sm text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Distribution Row
                </button>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end gap-3 pt-6 border-t">
              <button
                  type="button"
                  onClick={() => navigate('/batteries')}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                  type="submit"
                  disabled={saving}
                  className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all flex items-center gap-2 disabled:opacity-50"
              >
                {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Saving...
                    </>
                ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Update Battery Product
                    </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
  );
};

export default EditBattery;
